// Tiny in-memory + sessionStorage cache for rarely-changing GET data.
//
// Rationale: things like the current gym record (`gymAPI.getGymById`) and the
// gym's allowed menus (`GET /permissions/my-gym`) are read by many components on
// almost every page (Navbar, ThemeProvider, Sidebar, dashboards). They change
// rarely, so refetching them on every navigation wastes round-trips — painful on
// Render's free plan where the dominant cost is network/cold-start latency.
//
// `cachedGet(key, fetcher, ttlMs)` returns a cached value when it is still fresh,
// otherwise it runs `fetcher` once and caches the (already-resolved) value in both
// a module-level Map (survives navigation within the SPA) and sessionStorage
// (survives full page reloads within the tab). All sessionStorage access is wrapped
// in try/catch because it can throw (private mode, blocked storage, quota).

const memory = new Map();
const DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes
const PREFIX = 'cachedGet:';

const readSession = (key) => {
  try {
    const raw = sessionStorage.getItem(PREFIX + key);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (e) {
    return null;
  }
};

const writeSession = (key, entry) => {
  try {
    sessionStorage.setItem(PREFIX + key, JSON.stringify(entry));
  } catch (e) {
    /* storage full / unavailable — in-memory cache still works */
  }
};

const fresh = (entry, ttlMs) =>
  entry && typeof entry.ts === 'number' && Date.now() - entry.ts < ttlMs;

/**
 * @param {string} key      Stable cache key.
 * @param {() => Promise<any>} fetcher  Called only on a cache miss; must resolve
 *                                      to a JSON-serializable value.
 * @param {number} [ttlMs]  Time-to-live in ms (default 5 min).
 */
export const cachedGet = async (key, fetcher, ttlMs = DEFAULT_TTL) => {
  const mem = memory.get(key);
  if (fresh(mem, ttlMs)) return mem.value;

  const stored = readSession(key);
  if (fresh(stored, ttlMs)) {
    memory.set(key, stored);
    return stored.value;
  }

  const value = await fetcher();
  const entry = { ts: Date.now(), value };
  memory.set(key, entry);
  writeSession(key, entry);
  return value;
};

/** Drop one cached key (e.g. after a mutation), or all keys when called bare. */
export const invalidateCachedGet = (key) => {
  if (key) {
    memory.delete(key);
    try { sessionStorage.removeItem(PREFIX + key); } catch (e) { /* ignore */ }
    return;
  }
  memory.clear();
  try {
    Object.keys(sessionStorage)
      .filter((k) => k.startsWith(PREFIX))
      .forEach((k) => sessionStorage.removeItem(k));
  } catch (e) { /* ignore */ }
};
